import { useState } from 'react';
import { Zap, Loader2, MailCheck } from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';

/**
 * Sign in, register, or ask for a reset link.
 *
 * `forgot` is a mode of this form rather than its own route because the server
 * answers identically whether or not the address exists — there is nothing to
 * navigate to, only a message to show, and keeping it here means the email
 * field the user already typed carries over.
 */
export function LoginView() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // login | register | forgot
  const [sent, setSent] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'forgot') {
        const result = await api.auth.forgotPassword(email);
        setSent(result.message);
        return;
      }
      if (mode === 'login') await login(email, password);
      else await register(email, password, name);
    } catch (err) {
      // The server answers identically for an unknown email and a wrong
      // password — no account enumeration — so the copy stays generic too.
      setError(err.message || 'Could not sign you in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__brand">
          <Zap size={17} className="brand__bolt" />
          <span className="brand__name">Superchat</span>
          <span className="brand__tag">AGENCY</span>
        </div>

        <h1 className="login__title">
          {mode === 'login'
            ? 'Sign in to the console'
            : mode === 'register'
              ? 'Create your workspace'
              : 'Reset your password'}
        </h1>
        <p className="login__sub">
          {mode === 'forgot'
            ? 'Enter your address and we will send a link to choose a new password. The link works once and expires in an hour.'
            : 'One console for every app your agency supports — inbox, tickets, docs and the emergency channel.'}
        </p>

        {sent && (
          <div className="result result--ok">
            <MailCheck size={13} /> {sent}
          </div>
        )}

        {mode === 'register' && (
          <label className="field">
            <span className="field__label">NAME</span>
            <input
              className="field__control"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </label>
        )}

        <label className="field">
          <span className="field__label">EMAIL</span>
          <input
            className="field__control"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>

        {mode !== 'forgot' && (
          <label className="field">
            <span className="field__label">PASSWORD</span>
            <input
              className="field__control"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </label>
        )}

        {error && <div className="login__error">{error}</div>}

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy && <Loader2 size={14} className="spin" />}
          {mode === 'login'
            ? 'Sign in'
            : mode === 'register'
              ? 'Create account'
              : 'Send reset link'}
        </button>

        <div className="login__links">
          <button
            type="button"
            className="login__switch"
            onClick={() => {
              setMode(mode === 'register' ? 'login' : 'register');
              setError(null);
              setSent(null);
            }}
          >
            {mode === 'register'
              ? 'Already have an account? Sign in'
              : 'No account yet? Create one'}
          </button>

          <button
            type="button"
            className="login__switch"
            onClick={() => {
              setMode(mode === 'forgot' ? 'login' : 'forgot');
              setError(null);
              setSent(null);
            }}
          >
            {mode === 'forgot' ? 'Back to sign in' : 'Forgot your password?'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default LoginView;
